import { LayerInfo } from "../core/graphics/LayerInfo";
import { V } from "../core/Vector";

/**
 * Rendering layer system configuration.
 *
 * ## Layer System Overview
 *
 * The layer system organizes visual elements into distinct rendering groups with different
 * behaviors and properties. This enables:
 * - **Depth Sorting**: Automatic z-ordering without manual depth management
 * - **Parallax Effects**: Different layers can move at different rates relative to the camera
 * - **Batch Rendering**: Similar elements grouped for better performance
 * - **Visual Effects**: Filters and effects applied per-layer
 *
 * ## Layer Rendering Order
 *
 * Layers render in the order they appear in this object (JavaScript object iteration order):
 * 1. **main** - Background elements and world objects (renders first = back)
 * 2. **hud** - UI elements and interface (renders on top of main)
 * 3. **debugHud** - Debug information (renders last = front)
 *
 * ## Layer Properties
 *
 * Each layer is configured with specific behavior:
 *
 * ### Main Layer
 * - **Purpose**: Game world objects (entities, effects, terrain)
 * - **Parallax**: [1, 1] = moves normally with camera movement
 * - **Use for**: Players, enemies, bullets, world decorations
 *
 * ### HUD Layer
 * - **Purpose**: User interface elements that stay on screen
 * - **Parallax**: [0, 0] = completely ignores camera movement
 * - **Use for**: Health bars, score displays, menus, buttons
 *
 * ### Debug HUD Layer
 * - **Purpose**: Development and debugging information
 * - **Parallax**: [0, 0] = stays fixed for easy reading
 * - **Use for**: FPS counters, collision bounds, performance metrics
 *
 * ## Performance Implications
 *
 * - **Layer Count**: Keep minimal - each layer adds transformation overhead
 * - **Sprite Distribution**: Balance sprites across layers to avoid bottlenecks
 * - **Filter Usage**: Layer-specific filters are more efficient than per-sprite filters
 *
 * ## Adding New Layers
 *
 * To add a new layer:
 * 1. Add it to this LAYERS object in desired render order
 * 2. Configure appropriate parallax settings
 * 3. Update LayerName type automatically via satisfies clause
 * 4. Use the new layer name when creating sprites
 *
 * @example
 * ```typescript
 * // Adding a background layer that moves slower than the camera
 * background: new LayerInfo({ paralax: V(0.3, 0.3) })
 *
 * // Using layers in sprite creation
 * const player = loadGameSprite("hero.png", "main");
 * const ui = loadGameSprite("button.png", "hud");
 * const debug = createGraphics("debugHud");
 * ```
 */
export const LAYERS = {
  // Background effects layer - renders behind main layer
  background: new LayerInfo(),
  // Main game world layer - moves with camera
  main: new LayerInfo(),
  // User interface layer - stays fixed on screen
  ui: new LayerInfo({ paralax: V(0, 0) }),
  // HUD background layer - stays fixed on screen, renders behind HUD elements
  menuBackground: new LayerInfo({ paralax: V(0, 0) }),
  // Menu layer
  menu: new LayerInfo({ paralax: V(0, 0) }),
  // Debug overlay layer - stays fixed on screen, renders on top
  debugHud: new LayerInfo({ paralax: V(0, 0) }),
  // Mouse cursor. This should always be on top of everything else.
  cursor: new LayerInfo({ paralax: V(0, 0) }),
} satisfies { [key: string]: LayerInfo };

export type LayerName = keyof typeof LAYERS;

/**
 * The default layer for sprites that don't explicitly specify a layer.
 *
 * When creating sprites without specifying a layer, they will be automatically
 * placed in this layer. Set to "main" since most game objects belong in the
 * primary world layer that moves with the camera.
 *
 * @example
 * ```typescript
 * // These are equivalent:
 * const sprite1 = loadGameSprite("player.png");
 * const sprite2 = loadGameSprite("player.png", "main");
 * ```
 */
export const DEFAULT_LAYER: LayerName = "main";
